import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../Components/Footer/Footer";
import MediaEditor from "../../Components/MediaEditor/MediaEditor";
import siteConfig from "../../config/site";

const API_URL = siteConfig.apiUrl;

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings"); // bookings | roster | invites | media

  // Data
  const [bookings, setBookings] = useState([]);
  const [roster, setRoster] = useState([]);
  const [invites, setInvites] = useState([]);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [confirmLive, setConfirmLive] = useState(null);
  const [scheduleMedia, setScheduleMedia] = useState(null);
  const [schedStart, setSchedStart] = useState("");
  const [schedEnd, setSchedEnd] = useState("");

  // Applications
  const [applications, setApplications] = useState([]);
  const [appSubTab, setAppSubTab] = useState("applications"); // applications | invites
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePersonas, setInvitePersonas] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSendEmail, setInviteSendEmail] = useState(true);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      if (tab === "bookings") fetchBookings();
      if (tab === "roster") fetchRoster();
      if (tab === "invites") { fetchInvites(); fetchApplications(); }
      if (tab === "media") fetchMedia();
    }
  }, [tab, user]);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, { credentials: "include" });
      if (res.status === 401) {
        navigate("/login");
        return;
      }
      const data = await res.json();
      if (data.role !== "admin") {
        navigate("/profile");
        return;
      }
      setUser(data);
      setLoading(false);
    } catch {
      navigate("/login");
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${API_URL}/bookings`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (e) {
      console.error("Failed to fetch bookings:", e);
    }
  };

  const fetchRoster = async () => {
    try {
      const res = await fetch(`${API_URL}/entertainers/roster`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRoster(data.roster || []);
      }
    } catch (e) {
      console.error("Failed to fetch roster:", e);
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await fetch(`${API_URL}/invites`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch (e) {
      console.error("Failed to fetch invites:", e);
    }
  };

  const fetchMedia = async () => {
    try {
      const res = await fetch(`${API_URL}/media`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMedia(data.media || []);
      }
    } catch (e) {
      console.error("Failed to fetch media:", e);
    }
  };

  const handleUpload = async (e, category = "gallery") => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    setError("");

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);

      try {
        const res = await fetch(`${API_URL}/media/upload`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || `Failed to upload ${file.name}`);
        }
      } catch {
        setError(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setSuccess("Upload complete!");
    setTimeout(() => setSuccess(""), 3000);
    fetchMedia();
    e.target.value = "";
  };

  const handleMediaAction = async (id, action) => {
    try {
      if (action === "delete") {
        await fetch(`${API_URL}/media/${id}`, { method: "DELETE", credentials: "include" });
      } else {
        await fetch(`${API_URL}/media/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(action),
        });
      }
      fetchMedia();
    } catch {
      setError("Action failed");
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await fetch(`${API_URL}/applications`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setApplications(data.applications || []);
      }
    } catch (e) {
      console.error("Failed to fetch applications:", e);
    }
  };

  const handleApproveApp = async (id) => {
    setApprovingId(id);
    try {
      const res = await fetch(`${API_URL}/applications/${id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setSuccess("Application approved! Invite sent.");
        setTimeout(() => setSuccess(""), 4000);
        fetchApplications();
        fetchInvites();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Failed to approve");
      }
    } catch {
      setError("Failed to approve application");
    }
    setApprovingId(null);
  };

  const handleRejectApp = async (id) => {
    setRejectingId(id);
    try {
      const res = await fetch(`${API_URL}/applications/${id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sendEmail: true }),
      });
      if (res.ok) {
        setSuccess("Application rejected.");
        setTimeout(() => setSuccess(""), 3000);
        fetchApplications();
      }
    } catch {
      setError("Failed to reject");
    }
    setRejectingId(null);
  };

  const handleUpdateAppNotes = async (id, adminNotes) => {
    try {
      await fetch(`${API_URL}/applications/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ adminNotes, status: "reviewed" }),
      });
      fetchApplications();
    } catch {
      setError("Failed to save notes");
    }
  };

  const handleResendInvite = async (id) => {
    try {
      const res = await fetch(`${API_URL}/invites/${id}/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setSuccess("Invite email resent!");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError("Failed to resend");
      }
    } catch {
      setError("Failed to resend invite");
    }
  };

  const handleMediaSave = async (id, updates) => {
    try {
      await fetch(`${API_URL}/media/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      fetchMedia();
      setEditingMedia(null);
      setSuccess("Media updated!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to save edits");
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteSending(true);
    setError("");
    setInviteResult(null);

    const personaNames = invitePersonas
      .split(",")
      .map(p => p.trim())
      .filter(Boolean);

    try {
      const res = await fetch(`${API_URL}/invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: inviteEmail,
          performerName: inviteName,
          personaNames,
          sendEmail: inviteSendEmail,
          customMessage: inviteMessage,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setInviteResult(data.invite);
        setInviteEmail("");
        setInviteName("");
        setInvitePersonas("");
        setInviteMessage("");
        if (data.emailSent) {
          setSuccess("Invite created and email sent!");
        } else {
          setSuccess("Invite created! (Email not configured — copy link manually)");
        }
        setTimeout(() => setSuccess(""), 4000);
        setSuccess("Invite created! Share the link with the performer.");
        setTimeout(() => setSuccess(""), 5000);
        fetchInvites();
      } else {
        setError(data.error || "Failed to create invite");
      }
    } catch {
      setError("Failed to create invite");
    } finally {
      setInviteSending(false);
    }
  };

  const handleDeleteInvite = async (id) => {
    try {
      await fetch(`${API_URL}/invites/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      fetchInvites();
    } catch {
      setError("Failed to delete invite");
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setSuccess("Link copied to clipboard!");
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleLogout = async () => {
    await fetch(`${API_URL}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="bg-[#0a0a0f] min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      <div className="h-20" />

      <section className="py-8 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-gray-500 text-sm font-body mt-1">Exo Entertainment Management</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-400 text-sm font-body transition-colors"
            >
              Sign Out
            </button>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-400 text-sm font-body flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError("")} className="text-red-600 hover:text-red-400">✕</button>
            </div>
          )}
          {success && (
            <div className="mb-6 p-3 rounded-lg bg-green-900/30 border border-green-800/50 text-green-400 text-sm font-body">
              ✓ {success}
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-800 mb-6">
            {[
              { id: "bookings", label: "Bookings", icon: "📅" },
              { id: "roster", label: "Roster", icon: "💃" },
              { id: "invites", label: `Invites${applications.filter(a => a.status === "new").length ? ` (${applications.filter(a => a.status === "new").length})` : ""}`, icon: "✉️" },
              { id: "media", label: "Media", icon: "🖼️" },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 sm:px-6 py-3 text-sm font-body font-medium transition-colors ${
                  tab === t.id
                    ? "text-white border-b-2 border-purple-500"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                <span className="mr-2">{t.icon}</span>
                {t.label}
                {t.id === "bookings" && bookings.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-purple-900/50 text-purple-300 text-xs rounded-full">
                    {bookings.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Bookings Tab ── */}
          {tab === "bookings" && (
            <div>
              {bookings.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-4">📅</div>
                  <p className="text-gray-500 font-body">No booking inquiries yet.</p>
                  <p className="text-gray-600 text-sm font-body mt-2">
                    They'll show up here when clients submit the booking form.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bookings.map(b => (
                    <div key={b._id} className="bg-gray-900/40 border border-gray-800/50 rounded-xl p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-white font-heading text-lg">{b.clientName}</h3>
                          <p className="text-gray-400 text-sm font-body">
                            {b.clientEmail} {b.clientPhone && `· ${b.clientPhone}`}
                          </p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-body uppercase tracking-wider ${
                          b.status === "confirmed" ? "bg-green-900/40 text-green-400 border border-green-800/40" :
                          b.status === "inquiry" ? "bg-yellow-900/40 text-yellow-400 border border-yellow-800/40" :
                          b.status === "cancelled" ? "bg-red-900/40 text-red-400 border border-red-800/40" :
                          "bg-gray-800 text-gray-400 border border-gray-700"
                        }`}>
                          {b.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm font-body text-gray-400">
                        <span>📅 {new Date(b.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                        <span>🎭 {b.personaId?.name || "No preference"}</span>
                        <span>🎪 {b.serviceType || "—"}</span>
                        {b.guestCount > 1 && <span>👥 {b.guestCount} guests</span>}
                      </div>
                      {b.clientNotes && (
                        <p className="text-gray-500 text-sm font-body mt-3 pl-3 border-l-2 border-gray-800">
                          {b.clientNotes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Roster Tab ── */}
          {tab === "roster" && (
            <div>
              {roster.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-4">💃</div>
                  <p className="text-gray-500 font-body">No performers yet.</p>
                  <p className="text-gray-600 text-sm font-body mt-2">
                    <button onClick={() => setTab("invites")} className="text-purple-400 hover:text-purple-300">
                      Invite a performer →
                    </button>
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {roster.map(r => (
                    <div key={r.performer._id} className="bg-gray-900/40 border border-gray-800/50 rounded-xl p-5">
                      <div className="flex items-center gap-4 mb-4">
                        {r.performer.avatar ? (
                          <img src={r.performer.avatar} alt={r.performer.name} className="w-12 h-12 rounded-full border-2 border-purple-700/50" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-purple-900/40 flex items-center justify-center text-purple-300 font-heading text-lg">
                            {r.performer.name?.[0] || "?"}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-heading text-lg">{r.performer.name}</h3>
                          <p className="text-gray-500 text-sm font-body">{r.performer.email}</p>
                        </div>
                        <div className="text-right">
                          {r.performer.phone ? (
                            <p className="text-gray-300 text-sm font-body">{r.performer.phone}</p>
                          ) : (
                            <p className="text-gray-600 text-xs font-body italic">No phone set</p>
                          )}
                        </div>
                      </div>
                      {r.personas.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {r.personas.map(p => (
                            <div key={p._id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/60 border border-gray-700/40 rounded-lg">
                              <span className={`w-2 h-2 rounded-full ${p.active ? "bg-green-500" : "bg-gray-600"}`} />
                              <span className="text-white text-sm font-body">{p.name}</span>
                              {p.tagline && <span className="text-gray-500 text-xs font-body">· {p.tagline}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Invites Tab ── */}
          {tab === "invites" && (
            <div>
              {/* Sub-tabs */}
              <div className="flex gap-2 mb-6 border-b border-gray-800/50 pb-3">
                {[
                  { id: "applications", label: `📬 Applications${applications.filter(a => a.status === "new").length ? ` (${applications.filter(a => a.status === "new").length})` : ""}` },
                  { id: "invites", label: "✉️ Invites" },
                ].map(st => (
                  <button key={st.id} onClick={() => setAppSubTab(st.id)}
                    className={`px-4 py-2 text-sm font-body rounded-lg transition-colors ${
                      appSubTab === st.id ? "bg-purple-700 text-white" : "bg-gray-800/50 text-gray-400 hover:text-white"
                    }`}>{st.label}</button>
                ))}
              </div>

              {/* ── Applications Sub-tab ── */}
              {appSubTab === "applications" && (
                <div>
                  {applications.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-4">📬</div>
                      <p className="text-gray-500 font-body">No applications yet.</p>
                      <p className="text-gray-600 text-sm font-body mt-1">Applications from <a href="/hiring" className="text-purple-400 hover:text-purple-300">/hiring</a> will appear here.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.map(app => {
                        const statusColors = {
                          new: "bg-yellow-900/40 text-yellow-400 border-yellow-800/30",
                          reviewed: "bg-blue-900/40 text-blue-400 border-blue-800/30",
                          approved: "bg-green-900/40 text-green-400 border-green-800/30",
                          rejected: "bg-red-900/40 text-red-400 border-red-800/30",
                          invited: "bg-purple-900/40 text-purple-400 border-purple-800/30",
                        };
                        const borderColor = app.status === "new" ? "border-yellow-800/30" :
                          app.status === "invited" ? "border-purple-800/30" :
                          app.status === "approved" ? "border-green-800/30" :
                          app.status === "rejected" ? "border-red-800/30" : "border-gray-800/50";

                        return (
                          <div key={app._id} className={`bg-gray-900/40 border rounded-xl p-5 ${borderColor}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="flex items-center gap-3">
                                  <h4 className="text-white font-body font-semibold">{app.name}</h4>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-body ${statusColors[app.status]}`}>
                                    {app.status}
                                  </span>
                                </div>
                                <p className="text-gray-500 text-xs font-body mt-0.5">
                                  {app.email} {app.phone && `· ${app.phone}`} · Applied {new Date(app.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {/* Application details */}
                            <div className="space-y-2 mb-4">
                              {app.intro && (
                                <div>
                                  <span className="text-gray-500 text-xs font-body uppercase tracking-wider">Introduction</span>
                                  <p className="text-gray-300 text-sm font-body mt-0.5">{app.intro}</p>
                                </div>
                              )}
                              {app.experience && (
                                <div>
                                  <span className="text-gray-500 text-xs font-body uppercase tracking-wider">Experience</span>
                                  <p className="text-gray-300 text-sm font-body mt-0.5">{app.experience}</p>
                                </div>
                              )}
                              <div className="flex gap-6 text-sm">
                                {app.availability && (
                                  <div>
                                    <span className="text-gray-500 text-xs font-body uppercase tracking-wider">Availability</span>
                                    <p className="text-gray-300 font-body mt-0.5">{app.availability}</p>
                                  </div>
                                )}
                                {app.interest && (
                                  <div>
                                    <span className="text-gray-500 text-xs font-body uppercase tracking-wider">Interest</span>
                                    <p className="text-gray-300 font-body mt-0.5">{app.interest}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Admin notes */}
                            <div className="mb-3">
                              <textarea
                                defaultValue={app.adminNotes || ""}
                                placeholder="Admin notes..."
                                rows={2}
                                onBlur={(e) => {
                                  if (e.target.value !== (app.adminNotes || "")) {
                                    handleUpdateAppNotes(app._id, e.target.value);
                                  }
                                }}
                                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/30 rounded-lg text-gray-300 text-sm font-body placeholder-gray-600 focus:outline-none focus:border-purple-600 resize-none"
                              />
                            </div>

                            {/* Actions */}
                            {!["invited", "rejected"].includes(app.status) && (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleApproveApp(app._id)}
                                  disabled={approvingId === app._id}
                                  className="px-4 py-2 bg-green-700/60 hover:bg-green-700/80 text-green-300 text-xs font-body font-semibold rounded-lg transition-colors disabled:opacity-50">
                                  {approvingId === app._id ? "Approving..." : "✓ Approve & Invite"}
                                </button>
                                <button onClick={() => handleRejectApp(app._id)}
                                  disabled={rejectingId === app._id}
                                  className="px-4 py-2 bg-red-800/40 hover:bg-red-800/60 text-red-400 text-xs font-body rounded-lg transition-colors disabled:opacity-50">
                                  {rejectingId === app._id ? "..." : "✕ Reject"}
                                </button>
                              </div>
                            )}
                            {app.status === "invited" && app.inviteId && (
                              <p className="text-purple-400 text-xs font-body">
                                ✓ Invite sent — waiting for them to accept
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── Invites Sub-tab ── */}
              {appSubTab === "invites" && (
                <div>
                  {/* Invite Form */}
                  <div className="mb-6">
                    {!showInviteForm ? (
                      <button onClick={() => setShowInviteForm(true)}
                        className="px-5 py-2.5 bg-purple-700 hover:bg-purple-600 text-white font-body text-sm font-semibold rounded-lg transition-colors">
                        + Invite Performer
                      </button>
                    ) : (
                      <div className="bg-gray-900/40 border border-gray-800/50 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-heading text-lg text-white">Invite New Performer</h3>
                          <button onClick={() => { setShowInviteForm(false); setInviteResult(null); }}
                            className="text-gray-500 hover:text-gray-300 transition-colors">✕</button>
                        </div>

                        {inviteResult ? (
                          <div className="space-y-4">
                            <div className="p-4 bg-green-900/20 border border-green-800/40 rounded-lg">
                              <p className="text-green-400 text-sm font-body mb-3">
                                ✓ Invite created for <strong>{inviteResult.email}</strong>
                              </p>
                              <div className="flex items-center gap-2">
                                <input type="text" readOnly value={inviteResult.inviteUrl}
                                  className="flex-1 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm font-body font-mono" />
                                <button onClick={() => handleCopyLink(inviteResult.inviteUrl)}
                                  className="px-4 py-2 bg-gold text-black font-body text-sm font-semibold rounded-lg hover:bg-gold-light transition-colors whitespace-nowrap">
                                  Copy Link
                                </button>
                              </div>
                            </div>
                            <button onClick={() => setInviteResult(null)}
                              className="text-purple-400 hover:text-purple-300 text-sm font-body">Send another invite</button>
                          </div>
                        ) : (
                          <form onSubmit={handleSendInvite} className="space-y-4">
                            <div>
                              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Email Address *</label>
                              <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white placeholder-gray-600 focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600 font-body text-sm"
                                placeholder="performer@gmail.com" />
                              <p className="text-gray-600 text-xs mt-1 font-body">Must match the Google account they'll sign in with.</p>
                            </div>
                            <div>
                              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Performer Name</label>
                              <input type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white placeholder-gray-600 focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600 font-body text-sm"
                                placeholder="e.g. Luna Starr" />
                            </div>
                            <div>
                              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Persona Names</label>
                              <input type="text" value={invitePersonas} onChange={(e) => setInvitePersonas(e.target.value)}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white placeholder-gray-600 focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600 font-body text-sm"
                                placeholder="e.g. Luna, Sapphire (comma separated)" />
                            </div>
                            <div>
                              <label className="block text-gray-400 text-xs uppercase tracking-wider mb-1 font-body">Personal Message <span className="text-gray-600">(optional)</span></label>
                              <textarea value={inviteMessage} onChange={(e) => setInviteMessage(e.target.value)}
                                rows={2}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-700/50 text-white placeholder-gray-600 focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600 font-body text-sm resize-none"
                                placeholder="Add a personal note to the invite email..." />
                            </div>
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={inviteSendEmail} onChange={(e) => setInviteSendEmail(e.target.checked)}
                                  className="w-4 h-4 rounded accent-purple-600" />
                                <span className="text-gray-400 text-sm font-body">Send invite email</span>
                              </label>
                            </div>
                            <div className="flex items-center gap-3 pt-2">
                              <button type="submit" disabled={inviteSending}
                                className="px-5 py-2.5 bg-gold text-black font-body text-sm font-semibold rounded-lg hover:bg-gold-light transition-colors disabled:opacity-50">
                                {inviteSending ? "Creating..." : inviteSendEmail ? "Create & Send Invite" : "Create Invite"}
                              </button>
                              <button type="button" onClick={() => setShowInviteForm(false)}
                                className="text-gray-500 hover:text-gray-300 text-sm font-body transition-colors">Cancel</button>
                            </div>
                          </form>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Invites List */}
                  {invites.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-sm font-body">No invites sent yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invites.map(inv => {
                        const isUsed = !!inv.usedAt;
                        const isExpired = !isUsed && new Date(inv.expiresAt) < new Date();
                        const isPending = !isUsed && !isExpired;

                        return (
                          <div key={inv._id} className={`bg-gray-900/40 border rounded-xl p-4 ${
                            isUsed ? "border-green-800/30" : isExpired ? "border-red-800/30" : "border-gray-800/50"
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <p className="text-white font-body text-sm">{inv.email}</p>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-body ${
                                    isUsed ? "bg-green-900/40 text-green-400" :
                                    isExpired ? "bg-red-900/40 text-red-400" :
                                    "bg-yellow-900/40 text-yellow-400"
                                  }`}>
                                    {isUsed ? "Joined" : isExpired ? "Expired" : "Pending"}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-4 mt-1 text-gray-500 text-xs font-body">
                                  {inv.performerName && <span>{inv.performerName}</span>}
                                  {inv.personaNames?.length > 0 && <span>Personas: {inv.personaNames.join(", ")}</span>}
                                  <span>Sent {new Date(inv.createdAt).toLocaleDateString()}</span>
                                  {isUsed && inv.usedBy && <span>Joined by {inv.usedBy.name || inv.usedBy.email}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isPending && (
                                  <>
                                    <button onClick={() => handleResendInvite(inv._id)}
                                      className="text-blue-400 hover:text-blue-300 text-xs font-body transition-colors">
                                      📧 Resend
                                    </button>
                                    <button onClick={() => handleCopyLink(`${siteConfig.url}/join?invite=${inv.token}`)}
                                      className="text-purple-400 hover:text-purple-300 text-xs font-body transition-colors">
                                      Copy Link
                                    </button>
                                    <button onClick={() => handleDeleteInvite(inv._id)}
                                      className="text-gray-600 hover:text-red-400 text-xs font-body transition-colors">Revoke</button>
                                  </>
                                )}
                                {(isUsed || isExpired) && (
                                  <button onClick={() => handleDeleteInvite(inv._id)}
                                    className="text-gray-600 hover:text-red-400 text-xs font-body transition-colors">Remove</button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Media Tab ── */}
          {tab === "media" && (
            <div>
              {/* Upload controls */}
              <div className="flex flex-wrap gap-3 mb-6">
                <label className="px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white font-body text-sm font-semibold rounded-lg transition-colors cursor-pointer">
                  {uploading ? "Uploading..." : "📷 Upload Images"}
                  <input type="file" accept="image/*" multiple onChange={(e) => handleUpload(e, "gallery")}
                    className="hidden" disabled={uploading} />
                </label>
                <label className="px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white font-body text-sm font-semibold rounded-lg transition-colors cursor-pointer">
                  {uploading ? "Uploading..." : "🎬 Upload Hero Video"}
                  <input type="file" accept="video/*" onChange={(e) => handleUpload(e, "hero")}
                    className="hidden" disabled={uploading} />
                </label>
                <label className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-body text-sm font-semibold rounded-lg transition-colors cursor-pointer">
                  {uploading ? "Uploading..." : "👤 Upload Profile Photo"}
                  <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "profile")}
                    className="hidden" disabled={uploading} />
                </label>
              </div>

              {/* Pending approvals */}
              {media.filter(m => m.status === "pending").length > 0 && (
                <div className="mb-8">
                  <h3 className="text-yellow-400 text-sm font-body font-semibold uppercase tracking-wider mb-3">
                    ⏳ Pending Approval ({media.filter(m => m.status === "pending").length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {media.filter(m => m.status === "pending").map(m => (
                      <div key={m._id} className="bg-gray-900/40 border border-yellow-800/30 rounded-xl overflow-hidden">
                        {m.type === "image" ? (
                          <img src={m.url} alt={m.originalName} className="w-full h-32 object-cover" />
                        ) : (
                          <video src={m.url} className="w-full h-32 object-cover" muted />
                        )}
                        <div className="p-2">
                          <p className="text-white text-xs font-body truncate">{m.originalName}</p>
                          <p className="text-gray-500 text-xs font-body">
                            by {m.uploadedBy?.name || "Unknown"} · {m.category}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => handleMediaAction(m._id, { status: "approved" })}
                              className="flex-1 px-2 py-1 bg-green-800/40 text-green-400 text-xs rounded hover:bg-green-800/60 transition-colors">
                              ✓ Approve
                            </button>
                            <button onClick={() => handleMediaAction(m._id, { status: "rejected" })}
                              className="flex-1 px-2 py-1 bg-red-800/40 text-red-400 text-xs rounded hover:bg-red-800/60 transition-colors">
                              ✕ Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hero Videos — grouped by publish status */}
              {(() => {
                const heroMedia = media.filter(m => m.category === "hero");
                const drafts = heroMedia.filter(m => !m.publishStatus || m.publishStatus === "draft");
                const scheduled = heroMedia.filter(m => m.publishStatus === "scheduled");
                const live = heroMedia.filter(m => m.publishStatus === "live");
                const expired = heroMedia.filter(m => m.publishStatus === "expired");

                const renderVideoRow = (m) => (
                  <div key={m._id} className={`flex items-center gap-3 bg-gray-900/40 border rounded-lg p-3 ${
                    m.publishStatus === "live" ? "border-green-800/30" :
                    m.publishStatus === "scheduled" ? "border-blue-800/30" :
                    m.publishStatus === "expired" ? "border-red-800/30" :
                    "border-gray-800/50"
                  }`}>
                    <video src={m.url} className="w-24 h-14 object-cover rounded"
                      style={{
                        filter: m.filters ? `brightness(${m.filters.brightness || 100}%) contrast(${m.filters.contrast || 100}%) saturate(${m.filters.saturate || 100}%)` : "none",
                        objectPosition: m.position ? `${m.position.x || 50}% ${m.position.y || 50}%` : "center",
                      }} muted />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-body truncate">{m.originalName}</p>
                      <p className="text-gray-500 text-xs font-body">
                        {(m.size / 1024 / 1024).toFixed(1)}MB
                        {m.startTime && ` · From ${new Date(m.startTime).toLocaleDateString()}`}
                        {m.endTime && ` · Until ${new Date(m.endTime).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => setEditingMedia(m)}
                        className="text-purple-400 hover:text-purple-300 text-xs px-2 py-1 bg-purple-900/30 rounded transition-colors">✏️</button>

                      {/* Draft/Expired → Set Live or Schedule */}
                      {(!m.publishStatus || m.publishStatus === "draft" || m.publishStatus === "expired") && (
                        <>
                          <button onClick={() => setConfirmLive(m)}
                            className="text-green-400 hover:text-green-300 text-xs px-2 py-1 bg-green-900/30 rounded transition-colors">
                            🟢 Set Live
                          </button>
                          <button onClick={() => { setScheduleMedia(m); setSchedStart(""); setSchedEnd(""); }}
                            className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 bg-blue-900/30 rounded transition-colors">
                            🕐 Schedule
                          </button>
                        </>
                      )}

                      {/* Live → Unpublish */}
                      {m.publishStatus === "live" && (
                        <>
                          <button onClick={() => handleMediaAction(m._id, { order: Math.max(0, (m.order || 0) - 1) })}
                            className="text-gray-400 hover:text-white text-sm px-1">↑</button>
                          <button onClick={() => handleMediaAction(m._id, { order: (m.order || 0) + 1 })}
                            className="text-gray-400 hover:text-white text-sm px-1">↓</button>
                          <button onClick={() => handleMediaAction(m._id, { publishStatus: "draft" })}
                            className="text-yellow-400 hover:text-yellow-300 text-xs px-2 py-1 bg-yellow-900/30 rounded transition-colors">
                            ↩ Unpublish
                          </button>
                        </>
                      )}

                      {/* Scheduled → Cancel */}
                      {m.publishStatus === "scheduled" && (
                        <button onClick={() => handleMediaAction(m._id, { publishStatus: "draft", startTime: null, endTime: null })}
                          className="text-yellow-400 hover:text-yellow-300 text-xs px-2 py-1 bg-yellow-900/30 rounded transition-colors">
                          ↩ Cancel
                        </button>
                      )}

                      <button onClick={() => handleMediaAction(m._id, "delete")}
                        className="text-gray-600 hover:text-red-400 text-xs transition-colors">🗑</button>
                    </div>
                  </div>
                );

                return heroMedia.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-white text-sm font-body font-semibold uppercase tracking-wider mb-3">🎬 Hero Videos</h3>

                    {live.length > 0 && (
                      <div className="mb-4">
                        <p className="text-green-400 text-xs font-body font-semibold mb-2">🟢 LIVE ({live.length})</p>
                        <div className="space-y-2">{live.sort((a,b) => a.order - b.order).map(renderVideoRow)}</div>
                      </div>
                    )}
                    {scheduled.length > 0 && (
                      <div className="mb-4">
                        <p className="text-blue-400 text-xs font-body font-semibold mb-2">🕐 SCHEDULED ({scheduled.length})</p>
                        <div className="space-y-2">{scheduled.map(renderVideoRow)}</div>
                      </div>
                    )}
                    {drafts.length > 0 && (
                      <div className="mb-4">
                        <p className="text-gray-400 text-xs font-body font-semibold mb-2">📝 DRAFTS ({drafts.length})</p>
                        <div className="space-y-2">{drafts.map(renderVideoRow)}</div>
                      </div>
                    )}
                    {expired.length > 0 && (
                      <div className="mb-4">
                        <p className="text-red-400 text-xs font-body font-semibold mb-2">⏰ EXPIRED ({expired.length})</p>
                        <div className="space-y-2">{expired.map(renderVideoRow)}</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* All Images */}
              <div>
                <h3 className="text-white text-sm font-body font-semibold uppercase tracking-wider mb-3">
                  📷 Images ({media.filter(m => m.type === "image" && m.status !== "rejected").length})
                </h3>
                {media.filter(m => m.type === "image" && m.status !== "rejected").length === 0 ? (
                  <p className="text-gray-600 text-sm font-body py-8 text-center">No images uploaded yet.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {media.filter(m => m.type === "image" && m.status !== "rejected").map(m => {
                      const ps = m.publishStatus || "draft";
                      return (
                        <div key={m._id} className={`bg-gray-900/40 border rounded-xl overflow-hidden group ${
                          ps === "live" ? "border-green-800/30" : "border-gray-800/50"
                        }`}>
                          <div className="relative">
                            <img src={m.url} alt={m.originalName} className="w-full h-32 object-cover" />
                            {/* Status badge */}
                            <div className={`absolute top-1.5 right-1.5 text-xs px-1.5 py-0.5 rounded-full font-body ${
                              ps === "live" ? "bg-green-800/80 text-green-300" :
                              ps === "scheduled" ? "bg-blue-800/80 text-blue-300" :
                              ps === "expired" ? "bg-red-800/80 text-red-300" :
                              "bg-gray-800/80 text-gray-400"
                            }`}>{ps}</div>
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2">
                              <div className="flex gap-1.5">
                                <button onClick={() => setEditingMedia(m)}
                                  className="px-2 py-1 bg-purple-700/60 text-purple-200 text-xs rounded hover:bg-purple-700/80">✏️ Edit</button>
                                <a href={m.url} download className="px-2 py-1 bg-white/20 text-white text-xs rounded hover:bg-white/30">⬇</a>
                                <button onClick={() => handleMediaAction(m._id, "delete")}
                                  className="px-2 py-1 bg-red-800/60 text-red-300 text-xs rounded hover:bg-red-800/80">🗑</button>
                              </div>
                              <div className="flex gap-1.5">
                                {(ps === "draft" || ps === "expired") && (
                                  <>
                                    <button onClick={() => setConfirmLive(m)}
                                      className="px-2 py-1 bg-green-800/60 text-green-300 text-xs rounded hover:bg-green-800/80">🟢 Live</button>
                                    <button onClick={() => { setScheduleMedia(m); setSchedStart(""); setSchedEnd(""); }}
                                      className="px-2 py-1 bg-blue-800/60 text-blue-300 text-xs rounded hover:bg-blue-800/80">🕐 Schedule</button>
                                  </>
                                )}
                                {ps === "live" && (
                                  <button onClick={() => handleMediaAction(m._id, { publishStatus: "draft" })}
                                    className="px-2 py-1 bg-yellow-800/60 text-yellow-300 text-xs rounded hover:bg-yellow-800/80">↩ Unpublish</button>
                                )}
                                {ps === "scheduled" && (
                                  <button onClick={() => handleMediaAction(m._id, { publishStatus: "draft", startTime: null, endTime: null })}
                                    className="px-2 py-1 bg-yellow-800/60 text-yellow-300 text-xs rounded hover:bg-yellow-800/80">↩ Cancel</button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="p-2">
                            <p className="text-gray-400 text-xs font-body truncate">{m.originalName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-1.5 py-0.5 rounded font-body ${
                                m.category === "profile" ? "bg-purple-900/40 text-purple-300" :
                                m.category === "hero" ? "bg-gold/20 text-gold" :
                                "bg-gray-700 text-gray-400"
                              }`}>{m.category}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Confirm Set Live Modal */}
      {confirmLive && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setConfirmLive(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-heading text-xl mb-3">Confirm Publish</h3>
            <div className="mb-4">
              {confirmLive.type === "video" ? (
                <video src={confirmLive.url} className="w-full h-40 object-cover rounded-lg"
                  style={{
                    filter: confirmLive.filters ? `brightness(${confirmLive.filters.brightness || 100}%) contrast(${confirmLive.filters.contrast || 100}%) saturate(${confirmLive.filters.saturate || 100}%)` : "none",
                  }} autoPlay muted loop playsInline />
              ) : (
                <img src={confirmLive.url} alt="" className="w-full h-40 object-cover rounded-lg" />
              )}
            </div>
            <p className="text-gray-300 text-sm font-body mb-2">
              <strong>{confirmLive.originalName}</strong>
            </p>
            <p className="text-gray-400 text-sm font-body mb-6">
              This will make it visible on the live site immediately. Are you sure?
            </p>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  await handleMediaAction(confirmLive._id, { publishStatus: "live", status: "approved" });
                  setConfirmLive(null);
                  setSuccess("Published!");
                  setTimeout(() => setSuccess(""), 3000);
                }}
                className="flex-1 py-2.5 bg-green-600 hover:bg-green-500 text-white font-body font-semibold rounded-lg transition-colors"
              >
                🟢 Yes, Set Live
              </button>
              <button
                onClick={() => setConfirmLive(null)}
                className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 font-body rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleMedia && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setScheduleMedia(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-heading text-xl mb-3">Schedule Media</h3>
            <p className="text-gray-400 text-sm font-body mb-4">
              <strong>{scheduleMedia.originalName}</strong> — set when it goes live and when it expires.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-gray-300 text-xs font-body uppercase tracking-wider mb-1 block">Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={schedStart}
                  onChange={e => setSchedStart(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-body text-sm focus:outline-none focus:border-purple-500"
                />
                <p className="text-gray-600 text-xs font-body mt-1">When it becomes visible on the site</p>
              </div>
              <div>
                <label className="text-gray-300 text-xs font-body uppercase tracking-wider mb-1 block">End Date & Time <span className="text-gray-600">(optional)</span></label>
                <input
                  type="datetime-local"
                  value={schedEnd}
                  onChange={e => setSchedEnd(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white font-body text-sm focus:outline-none focus:border-purple-500"
                />
                <p className="text-gray-600 text-xs font-body mt-1">When it automatically goes offline (leave blank for no expiry)</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  if (!schedStart) {
                    setError("Start time is required");
                    return;
                  }
                  const update = {
                    publishStatus: "scheduled",
                    status: "approved",
                    startTime: new Date(schedStart).toISOString(),
                    endTime: schedEnd ? new Date(schedEnd).toISOString() : null,
                  };
                  await handleMediaAction(scheduleMedia._id, update);
                  setScheduleMedia(null);
                  setSuccess("Scheduled!");
                  setTimeout(() => setSuccess(""), 3000);
                }}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-body font-semibold rounded-lg transition-colors"
              >
                🕐 Schedule
              </button>
              <button
                onClick={() => setScheduleMedia(null)}
                className="flex-1 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 font-body rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />

      {/* Media Editor Modal */}
      {editingMedia && (
        <MediaEditor
          media={editingMedia}
          onSave={handleMediaSave}
          onClose={() => setEditingMedia(null)}
        />
      )}
    </div>
  );
};

export default Admin;
